/**
 * ApiStatusError - Error class for HTTP API errors with status codes
 * 
 * This error is thrown when the API returns an HTTP error status.
 * It includes the HTTP status code, detailed error message, trace ID for debugging,
 * and an optional title for user-friendly display.
 */
export class ApiStatusError extends Error {
  public readonly status: number;
  public readonly detail: string;
  public readonly traceId: string | undefined;
  public readonly title: string | undefined;
  public readonly instance: string;
  public readonly type: string;
  public readonly errors?: Record<string, string[]>;

  constructor(detail: string, status: number, traceId?: string, title?: string, instance?: string) {
    super(detail);
    this.name = 'ApiStatusError';
    this.status = status;
    this.detail = detail;
    this.instance = instance ?? 'Unknown endpoint';
    this.traceId = traceId;
    this.title = title;
    this.type = status >= 500 ? 'server_error' : 'client_error';
    this.errors = undefined;
  }

  private getProblemCategory(): string | null {
    if (this.isAuthError()) return 'auth';
    if (this.isServerError()) return 'server';
    if (this.isClientError()) return 'client';
    return null;
  }

  /**
   * Check if this is a client error (4xx)
   */
  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  /**
   * Check if this is an authentication error (401)
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    return this.title ?? this.detail ?? this.message;
  }

  /**
   * Get debug information including RFC 9457 details
   */
  getDebugInfo(): {
    status: number;
    type?: string;
    category?: string | null;
    instance: string;
    traceId?: string;
  } {
    return {
      status: this.status,
      type: this.type,
      category: this.getProblemCategory(),
      instance: this.instance,
      traceId: this.traceId,
    };
  }

  /**
   * Check if this error has detailed field-level validation errors
   */
  hasDetailedErrors(): boolean {
    return !!(
      this.errors && 
      typeof this.errors === 'object' && 
      Object.keys(this.errors).length > 0
    );
  }

  /**
   * Handle regex replacement for different field name patterns
   * @param match - The matched string from the regex
   * @param camelLower - Lowercase letter before uppercase (camelCase)
   * @param camelUpper - Uppercase letter after lowercase (camelCase)
   * @param consecutiveUpper1 - First uppercase in consecutive sequence
   * @param consecutiveUpper2 - Second uppercase followed by lowercase
   * @returns Replacement string with appropriate spacing
   */
  private handleFieldNameReplacement(
    match: string,
    camelLower?: string,
    camelUpper?: string,
    consecutiveUpper1?: string,
    consecutiveUpper2?: string
  ): string {
    // Handle separators (underscore, hyphen) - replace with space
    if (match === '_' || match === '-') {
      return ' ';
    }
    
    // Handle camelCase transitions (e.g., "firstName" -> "first Name")
    if (camelLower && camelUpper) {
      return `${camelLower} ${camelUpper}`;
    }
    
    // Handle consecutive uppercase letters (e.g., "XMLParser" -> "XML Parser")
    if (consecutiveUpper1 && consecutiveUpper2) {
      return `${consecutiveUpper1} ${consecutiveUpper2}`;
    }
    
    // Default: return match unchanged
    return match;
  }

  /**
   * Format field name for user-friendly display
   * Handles camelCase, snake_case, and kebab-case field names
   * @param field - The field name to format
   * @returns Human-readable field name
   */
  private formatFieldName(field: string): string {
    if (!field || field.length === 0) {
      return 'Unknown field';
    }

    // Apply field name transformations using the helper function
    // Regex matches: camelCase transitions (aB), consecutive uppercase (ABc), underscores, and hyphens
    // - ([a-z])([A-Z]): camelCase transition (e.g., "firstName" -> "first" + "Name")
    // - ([A-Z])([A-Z][a-z]): consecutive uppercase (e.g., "XMLParser" -> "XML" + "Parser")
    // - [_-]: underscores and hyphens to be replaced with spaces
    const formatted = field.replace(
      /([a-z])([A-Z])|([A-Z])([A-Z][a-z])|[_-]/g,
      (match: string, p1?: string, p2?: string, p3?: string, p4?: string) => {
        // Only first 5 parameters needed for field name replacement logic
        return this.handleFieldNameReplacement(match, p1, p2, p3, p4);
      }
    );
    
    // Common acronyms that should stay uppercase
    const acronyms = new Set(['ID', 'URL', 'API', 'HTTP', 'HTTPS', 'XML', 'HTML', 'JSON', 'CSS', 'JS', 'SQL', 'DB']);
    
    // Split into words and capitalize each word, preserving acronyms
    return formatted
      .split(' ')
      .map((word: string) => {
        const upperWord = word.toUpperCase();
        if (acronyms.has(upperWord)) {
          return upperWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  /**
   * Get formatted validation errors for display
   * @returns Array of formatted error messages
   */
  getFormattedErrors(): string[] {
    if (!this.hasDetailedErrors()) return [];
    
    const formattedErrors: string[] = [];
    for (const [field, messages] of Object.entries(this.errors as Record<string, string[]>)) {
      // Use the robust field name formatting
      const fieldName = this.formatFieldName(field);
      
      // Ensure messages is an array and handle potential null/undefined values
      const messageArray = Array.isArray(messages) ? messages : [];
      for (const message of messageArray) {
        if (message && typeof message === 'string') {
          formattedErrors.push(`${fieldName}: ${message}`);
        }
      }
    }
    return formattedErrors;
  }

  /**
   * Get the primary error message using RFC 9457 precedence order
   * @returns The most appropriate error message from available sources
   * @private
   */
  private getPrimaryErrorMessage(): string {
    // RFC 9457 recommends: detail (specific) -> title (general) -> message (fallback) -> default
    return this.detail ?? this.title ?? this.message ?? 'An error occurred';
  }

  /**
   * Get a comprehensive error message including detailed validation errors
   */
  getDetailedUserMessage(): string {
    // Get the primary error message using RFC 9457 precedence
    let message = this.getPrimaryErrorMessage();
    
    if (this.hasDetailedErrors()) {
      const detailedErrors = this.getFormattedErrors();
      if (detailedErrors.length > 0) {
        message += '\n\nDetailed issues:\n• ' + detailedErrors.join('\n• ');
      }
    }
    
    return message;
  }
}
